import { useAccess, useHasAccess } from './useAccess';
import { useDocumentProgress } from './useDocument';
import { useDocumentSurtitle } from './useSurtitle';
import { useDocumentUrlGenerator } from './useUrlGenerator';

export interface DocumentCardDocument extends Omit<Partial<DocumentSnippet>, 'type'> {
    type?: DocumentTypeString | 'native_ad' | null;
    externalUrl?: string | null;
}

export interface DocumentCardOptions {
    linkQuery?: Record<string, unknown> | null;
    placeholder?: boolean;
    surtitle?: Record<string, unknown>;
}

/**
 * Logic shared by the document cards: the link, the access, the reading progress, the surtitle
 * and the first category, author, brand and sponsor of the document. Each card passes the fields
 * it shows: the progress is only fetched with an `id`.
 */
function useDocumentCard(
    {
        id = null,
        type = null,
        slug = null,
        externalUrl = null,
        surtitle = null,
        brands = null,
        categories = null,
        credits = null,
        authors = null,
        sponsors = null,
        topic = null,
        format = null,
        topics = null,
        formats = null,
        collection = null,
        season = null,
        episode = null,
        micromagNumber = null,
        date = null,
        access = null,
    }: DocumentCardDocument,
    {
        linkQuery = null,
        placeholder = false,
        surtitle: surtitleOptions = {},
    }: DocumentCardOptions = {},
) {
    const url = useDocumentUrlGenerator();
    const href = externalUrl || url({ type, slug }, linkQuery) || null;

    const [category = null] = categories || [];
    const [{ author: creditAuthor = null } = {}] = credits || [];
    const [author = creditAuthor] = authors || [];
    const [brand = null] = brands || [];
    const { handle: brandHandle = null } = brand || {};
    const [sponsor = null] = sponsors || [];

    const finalAccess = useAccess({ collection, access });
    const { blocked = false } = finalAccess || {};
    const unblocked = useHasAccess(finalAccess);

    const { progress } = useDocumentProgress(!placeholder ? id : null);

    const finalSurtitle = useDocumentSurtitle(
        {
            type,
            surtitle,
            collection,
            topics: topics || (topic !== null ? [topic] : null),
            formats: formats || (format !== null ? [format] : null),
            season,
            episode,
            categories,
            micromagNumber,
            date,
        },
        surtitleOptions,
    );

    return {
        href,
        category,
        author,
        brandHandle,
        sponsor,
        blocked,
        unblocked,
        progress,
        surtitle: finalSurtitle,
    };
}

export default useDocumentCard;
